/**
 * AdvertiserDashboard - Performance Metrics Dashboard for Advertisers
 * Shows listing views, enquiry stats, response rates, and monthly trends
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, Eye, MessageCircle, Clock, Home, Star,
  ArrowUp, ArrowDown, Calendar, AlertCircle, CheckCircle2,
  Shield, BadgeCheck, Loader2, BarChart3, Activity,
  Users, Target, Award, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DocumentVerificationStatus from '../components/advertiser/DocumentVerificationStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat card component
const StatCard = ({ icon: Icon, label, value, subtext, trend, trendLabel, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600'
  };
  
  return (
    <Card data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-500">{label}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">{value}</span>
              {trend !== undefined && (
                <span className={`flex items-center text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {Math.abs(trend)}%
                </span>
              )}
            </div>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
            {trendLabel && <p className="text-xs text-gray-400 mt-1">{trendLabel}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Mini bar chart component
const MiniBarChart = ({ data, height = 100 }) => {
  const maxValue = Math.max(...data.map(d => Math.max(d.enquiries || 0, d.views || 0)), 1);
  
  return (
    <div className="flex items-end gap-2 h-full" style={{ height }}>
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex gap-0.5 items-end justify-center" style={{ height: height - 20 }}>
            <div 
              className="w-3 bg-blue-500 rounded-t"
              style={{ height: `${(item.views / maxValue) * 100}%`, minHeight: 2 }}
            />
            <div 
              className="w-3 bg-green-500 rounded-t"
              style={{ height: `${(item.enquiries / maxValue) * 100}%`, minHeight: 2 }}
            />
          </div>
          <span className="text-xs text-gray-400">{item.month?.split(' ')[0]}</span>
        </div>
      ))}
    </div>
  );
};

// Response time indicator
const ResponseTimeIndicator = ({ displayText, averageMinutes }) => {
  let color = 'text-green-600';
  let bgColor = 'bg-green-100';
  
  if (averageMinutes > 1440) { // > 24 hours
    color = 'text-red-600';
    bgColor = 'bg-red-100';
  } else if (averageMinutes > 360) { // > 6 hours
    color = 'text-amber-600';
    bgColor = 'bg-amber-100';
  }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${bgColor} ${color}`}>
      <Clock className="w-4 h-4" />
      <span className="text-sm font-medium">{displayText}</span>
    </div>
  );
};

export default function AdvertiserDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [advertiserProfile, setAdvertiserProfile] = useState(null);
  const [listings, setListings] = useState([]);
  
  // Get user email from localStorage
  const getUserEmail = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.email;
      }
    } catch (e) {
      console.error('Error getting user:', e);
    }
    return null;
  };
  
  const user = { email: getUserEmail() };
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }
      
      try {
        // First get advertiser profile by email
        const profileRes = await axios.get(`${API_URL}/api/advertisers/by-email/${encodeURIComponent(user.email)}`);
        setAdvertiserProfile(profileRes.data);
        
        const advertiserId = profileRes.data.id;
        
        // Fetch metrics
        const metricsRes = await axios.get(`${API_URL}/api/advertisers/${advertiserId}/metrics`);
        setMetrics(metricsRes.data);
        
        // Fetch listings
        const listingsRes = await axios.get(`${API_URL}/api/advertisers/${advertiserId}/listings`);
        setListings(listingsRes.data.listings || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status === 404) {
          // No advertiser profile - redirect to onboarding
          toast.info('Please complete your advertiser profile first');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user?.email]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-xl font-semibold mb-2">Login Required</h2>
          <p className="text-gray-600 mb-4">Please log in to view your advertiser dashboard.</p>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </Card>
      </div>
    );
  }
  
  if (!advertiserProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md text-center p-8">
          <Users className="w-12 h-12 mx-auto mb-4 text-blue-500" />
          <h2 className="text-xl font-semibold mb-2">Become an Advertiser</h2>
          <p className="text-gray-600 mb-4">Create your advertiser profile to start listing properties and track your performance.</p>
          <Button onClick={() => navigate('/advertiser-onboarding')}>
            Get Started
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="advertiser-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Performance Dashboard</h1>
              <p className="text-blue-100 mt-1">Welcome back, {advertiserProfile.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-white/20 text-white border-white/30">
                {advertiserProfile.verification_level >= 2 ? (
                  <><Shield className="w-3 h-3 mr-1" /> Business Verified</>
                ) : advertiserProfile.verification_level >= 1 ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Identity Verified</>
                ) : (
                  'Unverified'
                )}
              </Badge>
              <Button variant="secondary" size="sm" onClick={() => navigate('/post-ad')}>
                Post New Ad
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Home}
            label="Active Listings"
            value={metrics?.listings?.active || 0}
            subtext={`${metrics?.listings?.total || 0} total listings`}
            color="blue"
          />
          <StatCard
            icon={Eye}
            label="Total Views"
            value={metrics?.views?.total?.toLocaleString() || '0'}
            subtext="Last 30 days"
            trend={12}
            color="purple"
          />
          <StatCard
            icon={MessageCircle}
            label="Enquiries"
            value={metrics?.enquiries?.total || 0}
            subtext={`${metrics?.enquiries?.responded || 0} responded`}
            color="green"
          />
          <StatCard
            icon={Target}
            label="Response Rate"
            value={`${metrics?.enquiries?.response_rate || 0}%`}
            subtext="Keep it above 90%"
            color="amber"
          />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Monthly Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Monthly Trends
                </CardTitle>
                <CardDescription>Views and enquiries over the last 6 months</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500" />
                    <span className="text-sm text-gray-600">Views</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span className="text-sm text-gray-600">Enquiries</span>
                  </div>
                </div>
                {metrics?.monthly_stats?.length > 0 ? (
                  <MiniBarChart data={metrics.monthly_stats} height={150} />
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-gray-400">
                    No data yet
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Recent Listings */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600" />
                    Your Listings
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/my-listings')}>
                    View All
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-8">
                    <Home className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No listings yet</p>
                    <Button className="mt-4" onClick={() => navigate('/post-ad')}>
                      Create Your First Listing
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {listings.slice(0, 5).map((listing) => (
                      <div 
                        key={listing.id || listing._id}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/listing/${listing.id || listing._id}`)}
                      >
                        {listing.images?.[0] ? (
                          <img 
                            src={listing.images[0]} 
                            alt={listing.title}
                            className="w-16 h-12 rounded object-cover"
                          />
                        ) : (
                          <div className="w-16 h-12 rounded bg-gray-100 flex items-center justify-center">
                            <Home className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{listing.title}</p>
                          <p className="text-sm text-gray-500">{listing.location}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-blue-600">
                            Rs. {listing.price?.toLocaleString()}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" /> {listing.views || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" /> {listing.enquiries || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Response Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Response Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponseTimeIndicator 
                  displayText={metrics?.response_time?.display || 'No data'}
                  averageMinutes={metrics?.response_time?.average_minutes}
                />
                <p className="text-sm text-gray-500 mt-3">
                  Fast responses improve your ranking and build trust with tenants.
                </p>
              </CardContent>
            </Card>
            
            {/* Document Verification Status - Full Component */}
            <DocumentVerificationStatus 
              advertiserId={advertiserProfile?.id}
              advertiserType={advertiserProfile?.type}
              onLevelChange={(newLevel) => {
                if (newLevel !== advertiserProfile?.verification_level) {
                  setAdvertiserProfile(prev => ({ ...prev, verification_level: newLevel }));
                }
              }}
            />
            
            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  Performance Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-gray-600">Add high-quality photos to increase views by 2x</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-gray-600">Respond to enquiries within 2 hours</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="text-gray-600">Keep your listings updated regularly</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
